import { PROTOCOL_VERSION } from '../core';
import type { ModuleSummary, SessionRecord, TrialResult } from '../core/types';
import { getTrainingModule } from '../training/modules';
import { summarizeModuleResults } from '../training/summarize';
import { DATABASE_SCHEMA_VERSION, Scan90Database, scan90Database } from './database';
import type {
  ImportableScan90Backup,
  MetadataRecord,
  Scan90Backup,
  SettingsRecord,
  StoredModuleSummary,
} from './types';
import { validateImportableBackup } from './validation';

function byTimestamp<T>(field: keyof T): (left: T, right: T) => number {
  return (left, right) => String(left[field]).localeCompare(String(right[field]));
}

export async function createBackup(database: Scan90Database = scan90Database): Promise<Scan90Backup> {
  const snapshot = await database.transaction(
      'r',
      [
        database.calibrations,
        database.environments,
        database.sessions,
        database.trials,
        database.summaries,
        database.settings,
        database.metadata,
      ],
      async () => ({
        calibrations: await database.calibrations.toArray(),
        environments: await database.environments.toArray(),
        sessions: await database.sessions.toArray(),
        trials: await database.trials.toArray(),
        summaries: await database.summaries.toArray(),
        settings: await database.settings.toArray(),
        metadata: await database.metadata.toArray(),
      }),
    );
  return {
    app: 'SCAN90 LAB',
    schemaVersion: DATABASE_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    data: {
      calibrations: snapshot.calibrations.sort(byTimestamp('createdAt')),
      environments: snapshot.environments.sort(byTimestamp('capturedAt')),
      sessions: snapshot.sessions.sort(byTimestamp('startedAt')),
      trials: snapshot.trials.sort(byTimestamp('startedAt')),
      summaries: snapshot.summaries.sort((left, right) => left.id.localeCompare(right.id)),
      settings: snapshot.settings.sort((left, right) => left.key.localeCompare(right.key)),
      metadata: snapshot.metadata.sort((left, right) => left.key.localeCompare(right.key)),
    },
  };
}

export async function exportAllJson(database: Scan90Database = scan90Database): Promise<string> {
  return JSON.stringify(await createBackup(database), null, 2);
}

function requireUniqueIds(entries: readonly { id: string }[], collection: string): void {
  const ids = new Set<string>();
  for (const entry of entries) {
    if (ids.has(entry.id)) throw new Error(`Invalid backup: duplicate ${collection} id ${entry.id}`);
    ids.add(entry.id);
  }
}

function parseBackup(json: string): ImportableScan90Backup {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('Invalid backup: the file is not valid JSON');
  }
  const backup = validateImportableBackup(parsed);
  requireUniqueIds(backup.data.calibrations, 'calibration');
  requireUniqueIds(backup.data.environments, 'environment');
  requireUniqueIds(backup.data.sessions, 'session');
  requireUniqueIds(backup.data.trials, 'trial');
  if (backup.schemaVersion === 2) requireUniqueIds(backup.data.summaries, 'summary');
  return backup;
}

function normalizeBackup(backup: ImportableScan90Backup): Scan90Backup {
  const now = new Date().toISOString();
  const trialsBySession = new Map<string, TrialResult[]>();
  for (const trial of backup.data.trials) {
    const trials = trialsBySession.get(trial.sessionId) ?? [];
    trials.push(trial);
    trialsBySession.set(trial.sessionId, trials);
  }
  const summaries: StoredModuleSummary[] = backup.schemaVersion === 2 ? backup.data.summaries : [];
  const settings: SettingsRecord[] = backup.data.settings ?? [];
  const metadata: MetadataRecord[] =
    backup.schemaVersion === 2
      ? backup.data.metadata
      : [{ key: 'importedFromSchema', value: 1, updatedAt: now }];
  return {
    app: 'SCAN90 LAB',
    schemaVersion: 2,
    exportedAt: backup.exportedAt,
    data: {
      calibrations: backup.data.calibrations,
      environments: backup.data.environments,
      sessions: backup.data.sessions.map((session) => {
        const sessionTrials = trialsBySession.get(session.id) ?? [];
        const legacyModuleIds = [...new Set(sessionTrials.map(({ moduleId }) => moduleId))];
        return {
          ...session,
          // Schema v1 predates these denormalized indexes. Rebuild them from
          // the raw trials so old exports receive the same relation checks as v2.
          moduleIds:
            backup.schemaVersion === 1 ? legacyModuleIds : (session.moduleIds ?? []),
          trialIds:
            backup.schemaVersion === 1
              ? sessionTrials.map(({ id }) => id)
              : (session.trialIds ?? []),
        };
      }),
      trials: backup.data.trials.map((trial) => ({ ...trial, metadata: trial.metadata ?? {} })),
      summaries,
      settings,
      metadata,
    },
  };
}

function validateRelations(backup: Scan90Backup): void {
  const importedFromV1 = backup.data.metadata.some(
    (record) => record.key === 'importedFromSchema' && record.value === 1,
  );
  const calibrationIds = new Set(backup.data.calibrations.map(({ id }) => id));
  const environmentIds = new Set(backup.data.environments.map(({ id }) => id));
  const environmentById = new Map(backup.data.environments.map((environment) => [environment.id, environment]));
  const sessionById = new Map(backup.data.sessions.map((session) => [session.id, session]));
  const trialById = new Map(backup.data.trials.map((trial) => [trial.id, trial]));
  const trialsBySession = new Map<string, TrialResult[]>();
  for (const trial of backup.data.trials) {
    const trials = trialsBySession.get(trial.sessionId) ?? [];
    trials.push(trial);
    trialsBySession.set(trial.sessionId, trials);
  }
  for (const environment of backup.data.environments) {
    if (environment.calibrationId && !calibrationIds.has(environment.calibrationId)) {
      throw new Error(`Invalid backup: missing calibration ${environment.calibrationId}`);
    }
  }
  for (const session of backup.data.sessions) {
    if (!environmentIds.has(session.environmentId)) {
      throw new Error(`Invalid backup: missing environment ${session.environmentId}`);
    }
    const environment = environmentById.get(session.environmentId);
    if (session.environmentConditionId && session.environmentConditionId !== (environment?.conditionId ?? environment?.id)) {
      throw new Error(`Invalid backup: session ${session.id} condition does not match its environment`);
    }
    if (new Set(session.moduleIds).size !== session.moduleIds.length) {
      throw new Error(`Invalid backup: duplicate module in session ${session.id}`);
    }
    if (new Set(session.trialIds).size !== session.trialIds.length) {
      throw new Error(`Invalid backup: duplicate trial reference in session ${session.id}`);
    }
    for (const trialId of session.trialIds) {
      const trial = trialById.get(trialId);
      if (!trial) throw new Error(`Invalid backup: missing trial ${trialId}`);
      if (trial.sessionId !== session.id) {
        throw new Error(`Invalid backup: trial ${trialId} belongs to another session`);
      }
    }
    if (!importedFromV1 && session.protocolVersion === PROTOCOL_VERSION && session.status === 'completed' && session.mode === 'training' && session.moduleIds.length > 0) {
      const summaryCount = backup.data.summaries.filter((summary) => summary.sessionId === session.id).length;
      if (summaryCount !== session.moduleIds.length) {
        throw new Error(`Invalid backup: completed session ${session.id} has an inconsistent summary count`);
      }
    }
  }
  for (const trial of backup.data.trials) {
    const session = sessionById.get(trial.sessionId);
    if (!session) {
      throw new Error(`Invalid backup: missing session ${trial.sessionId}`);
    }
    if (!environmentIds.has(trial.environmentId)) {
      throw new Error(`Invalid backup: missing environment ${trial.environmentId}`);
    }
    if (trial.environmentId !== session.environmentId) {
      throw new Error(`Invalid backup: trial ${trial.id} uses a different environment from its session`);
    }
    if (trial.protocolVersion !== session.protocolVersion) {
      throw new Error(`Invalid backup: trial ${trial.id} uses a different protocol from its session`);
    }
    if (!session.moduleIds.includes(trial.moduleId)) {
      throw new Error(`Invalid backup: trial ${trial.id} module is not included in its session`);
    }
    if (!session.trialIds.includes(trial.id)) {
      throw new Error(`Invalid backup: session ${session.id} does not reference trial ${trial.id}`);
    }
  }
  for (const summary of backup.data.summaries) {
    const session = sessionById.get(summary.sessionId);
    if (!session) {
      throw new Error(`Invalid backup: missing session ${summary.sessionId}`);
    }
    if (summary.id !== `${summary.sessionId}:${summary.moduleId}`) {
      throw new Error(`Invalid backup: summary ${summary.id} has an inconsistent id`);
    }
    if (!session.moduleIds.includes(summary.moduleId)) {
      throw new Error(`Invalid backup: summary ${summary.id} module is not included in its session`);
    }
    if (session.protocolVersion === PROTOCOL_VERSION) {
      const expected = summarizeModuleResults(
        getTrainingModule(summary.moduleId),
        trialsBySession.get(summary.sessionId) ?? [],
      );
      if (!summariesMatch(summary, expected)) {
        throw new Error(`Invalid backup: summary ${summary.id} does not match its raw trials`);
      }
    }
  }
  for (const record of backup.data.settings) {
    if (record.value.calibrationId && !calibrationIds.has(record.value.calibrationId)) {
      throw new Error(`Invalid backup: missing calibration ${record.value.calibrationId}`);
    }
  }
}

function numbersMatch(left: number | undefined, right: number | undefined): boolean {
  if (left === undefined || right === undefined) return left === right;
  return Math.abs(left - right) <= 0.0001;
}

function pairsMatch(
  left: readonly [number, number] | undefined,
  right: readonly [number, number] | undefined,
): boolean {
  if (!left || !right) return left === right;
  return numbersMatch(left[0], right[0]) && numbersMatch(left[1], right[1]);
}

function summariesMatch(stored: StoredModuleSummary, expected: ModuleSummary): boolean {
  return stored.moduleId === expected.moduleId
    && stored.validTrials === expected.validTrials
    && numbersMatch(stored.accuracy, expected.accuracy)
    && pairsMatch(stored.accuracyCi95, expected.accuracyCi95)
    && numbersMatch(stored.medianReactionTimeMs, expected.medianReactionTimeMs)
    && pairsMatch(stored.reactionTimeIqrMs, expected.reactionTimeIqrMs)
    && stored.omissions === expected.omissions
    && stored.falseResponses === expected.falseResponses
    && numbersMatch(stored.threshold, expected.threshold)
    && stored.thresholdUnit === expected.thresholdUnit
    && conditionSummariesMatch(stored.conditionSummaries, expected.conditionSummaries);
}

function conditionSummariesMatch(
  stored: ModuleSummary['conditionSummaries'],
  expected: ModuleSummary['conditionSummaries'],
): boolean {
  if (!stored || !expected) return stored === expected;
  if (stored.length !== expected.length) return false;
  return stored.every((condition, index) => {
    const candidate = expected[index];
    return candidate !== undefined
      && condition.key === candidate.key
      && condition.label === candidate.label
      && condition.validTrials === candidate.validTrials
      && numbersMatch(condition.accuracy, candidate.accuracy)
      && numbersMatch(condition.medianReactionTimeMs, candidate.medianReactionTimeMs);
  });
}

export interface BackupPreview {
  schemaVersion: 1 | 2;
  exportedAt: string;
  calibrationCount: number;
  environmentCount: number;
  sessionCount: number;
  trialCount: number;
  summaryCount: number;
  profileName?: string;
  age?: number;
}

/** Fully validates a backup without mutating IndexedDB. */
export function inspectBackupJson(json: string): BackupPreview {
  const parsed = parseBackup(json);
  const backup = normalizeBackup(parsed);
  validateRelations(backup);
  const profile = backup.data.settings.find(({ key }) => key === 'app')?.value;
  return {
    schemaVersion: parsed.schemaVersion,
    exportedAt: backup.exportedAt,
    calibrationCount: backup.data.calibrations.length,
    environmentCount: backup.data.environments.length,
    sessionCount: backup.data.sessions.length,
    trialCount: backup.data.trials.length,
    summaryCount: backup.data.summaries.length,
    profileName: profile?.profileName,
    age: profile?.age,
  };
}

export interface ImportOptions {
  /** Replace existing data by default. Set false to merge by primary key. */
  replace?: boolean;
}

export async function importAllJson(
  json: string,
  options: ImportOptions = {},
  database: Scan90Database = scan90Database,
): Promise<Scan90Backup> {
  const backup = normalizeBackup(parseBackup(json));
  validateRelations(backup);
  const replace = options.replace ?? true;
  await database.transaction(
    'rw',
    [
      database.calibrations,
      database.environments,
      database.sessions,
      database.trials,
      database.summaries,
      database.settings,
      database.metadata,
    ],
    async () => {
      if (replace) {
        await Promise.all([
          database.calibrations.clear(),
          database.environments.clear(),
          database.sessions.clear(),
          database.trials.clear(),
          database.summaries.clear(),
          database.settings.clear(),
          database.metadata.clear(),
        ]);
      }
      await database.calibrations.bulkPut(backup.data.calibrations);
      await database.environments.bulkPut(backup.data.environments);
      await database.sessions.bulkPut(backup.data.sessions);
      await database.trials.bulkPut(backup.data.trials);
      await database.summaries.bulkPut(backup.data.summaries);
      await database.settings.bulkPut(backup.data.settings);
      await database.metadata.bulkPut(backup.data.metadata);
    },
  );
  return backup;
}

function csvCell(value: unknown): string {
  if (value === undefined || value === null) return '';
  let text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  // Spreadsheet formula injection protection for user-controlled response/metadata strings.
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

const TRIAL_CSV_COLUMNS = [
  'session_id',
  'session_mode',
  'session_status',
  'trial_id',
  'module_id',
  'protocol_version',
  'seed',
  'stimulus_id',
  'started_at',
  'stimulus_onset_ms',
  'response_at_ms',
  'response',
  'correct_response',
  'correct',
  'reaction_time_ms',
  'difficulty',
  'valid',
  'invalid_reason',
  'environment_id',
  'metadata_json',
] as const;

function trialCsvRow(trial: TrialResult, session?: SessionRecord): unknown[] {
  return [
    trial.sessionId,
    session?.mode,
    session?.status,
    trial.id,
    trial.moduleId,
    trial.protocolVersion,
    trial.seed,
    trial.stimulusId,
    trial.startedAt,
    trial.stimulusOnsetMs,
    trial.responseAtMs,
    trial.response,
    trial.correctResponse,
    trial.correct,
    trial.reactionTimeMs,
    trial.difficulty,
    trial.valid,
    trial.invalidReason,
    trial.environmentId,
    trial.metadata,
  ];
}

export async function exportTrialsCsv(database: Scan90Database = scan90Database): Promise<string> {
  const [trials, sessions] = await Promise.all([
    database.trials.orderBy('startedAt').toArray(),
    database.sessions.toArray(),
  ]);
  const sessionById = new Map(sessions.map((session) => [session.id, session]));
  const rows = [
    TRIAL_CSV_COLUMNS.join(','),
    ...trials.map((trial) =>
      trialCsvRow(trial, sessionById.get(trial.sessionId)).map(csvCell).join(','),
    ),
  ];
  return `\uFEFF${rows.join('\r\n')}\r\n`;
}

export function downloadTextFile(contents: string, filename: string, mimeType: string): void {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new Error('Downloads are only available in a browser');
  }
  const url = URL.createObjectURL(new Blob([contents], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

export async function downloadJsonBackup(database: Scan90Database = scan90Database): Promise<void> {
  downloadTextFile(
    await exportAllJson(database),
    `scan90-lab-backup-${dateStamp()}.json`,
    'application/json;charset=utf-8',
  );
}

export async function downloadTrialsCsv(database: Scan90Database = scan90Database): Promise<void> {
  downloadTextFile(
    await exportTrialsCsv(database),
    `scan90-lab-trials-${dateStamp()}.csv`,
    'text/csv;charset=utf-8',
  );
}
